import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { EventTime } from 'components/common/EventTime'
import { useTimezone } from 'components/contexts/timezone.context'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { AssignmentDTO, AssignmentDTOAdministrator, AssignmentDTOCreative } from 'models/assignment'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getMeetingTypeProduct } from 'utils/helpers'
import { DetailCommentBlock, DetailField, DetailSection } from '../common'
import { AssignmentAddonsSection } from './shared/AssignmentAddonsSection.component'
import { AssignmentRendererProps } from './AssignmentRenderer.types'

const hasCreativeFields = (a: AssignmentDTO): a is AssignmentDTOCreative | AssignmentDTOAdministrator =>
  'contactPerson' in a

const hasAdminFields = (a: AssignmentDTO): a is AssignmentDTOAdministrator =>
  'shootingInstructions' in a

/**
 * Renderer for photo and video on-site shooting assignments.
 * Displays shooting instructions, logistics/schedule, contact person, and meeting instructions.
 * All data comes from the assignment DTO — no additional endpoint needed.
 */
export const PhotoVideoDetailsRenderer: FC<AssignmentRendererProps> = ({ assignment, order, mainProductId }) => {
  const { t } = useTranslation(['gallery', 'product_kind'])
  const { userTimezone } = useTimezone()

  const shootingInstructions = hasAdminFields(assignment) ? assignment.shootingInstructions : undefined
  const creativeData = hasCreativeFields(assignment) ? assignment : undefined
  const contactPerson = creativeData?.contactPerson
  const meetingInstructions = creativeData?.meetingInstructions

  const meetingTypeProduct = useMemo(
    () => getMeetingTypeProduct(order?.financials.products, [assignment]),
    [assignment, order?.financials.products]
  )

  return (
    <Stack gap="0.8rem">

      {!!shootingInstructions &&
        <DetailSection title={t('gallery:assignment_details.photo_video.shooting_instructions')}>
          <DetailCommentBlock>{shootingInstructions}</DetailCommentBlock>
        </DetailSection>
      }

      <DetailSection title={t('gallery:assignment_details.photo_video.logistics.title')}>

        <DetailField
          label={t('gallery:assignment_details.photo_video.logistics.meeting_type')}
          value={meetingTypeProduct ? t(`product_kind:${meetingTypeProduct.kind}`) : undefined}
        />

        {!!assignment.shootingStartDateTime &&
          <DetailField label={t('gallery:assignment_details.photo_video.logistics.date_and_time')}>
            <EventTime
              eventStart={assignment.shootingStartDateTime}
              eventTimezone={assignment.timezone}
              userTimezone={userTimezone}
              minDuration={assignment.timeOnSiteMin}
              maxDuration={assignment.timeOnSiteMax}
              size="md"
            />
          </DetailField>
        }

        {!!contactPerson &&
          <DetailField label={t('gallery:assignment_details.photo_video.logistics.contact_person')}>
            <Stack gap="0.2rem">
              {!!contactPerson.name &&
                <Box fontWeight={450} fontSize="1.6rem" color={GRAY_900}>{contactPerson.name}</Box>
              }
              {!!contactPerson.email &&
                <Box fontSize="1.6rem" fontWeight={350} color={GRAY_900}>
                  <Box component="span" color={GRAY_700}>{t('gallery:assignment_details.photo_video.logistics.email')}: </Box>
                  {contactPerson.email}
                </Box>
              }
              {!!contactPerson.phone &&
                <Box fontSize="1.6rem" fontWeight={350} color={GRAY_900}>
                  <Box component="span" color={GRAY_700}>{t('gallery:assignment_details.photo_video.logistics.phone')}: </Box>
                  {contactPerson.phone}
                </Box>
              }
            </Stack>
          </DetailField>
        }

        {!!meetingInstructions &&
          <DetailField label={t('gallery:assignment_details.photo_video.meeting_instructions')}>
            <DetailCommentBlock>{meetingInstructions}</DetailCommentBlock>
          </DetailField>
        }

      </DetailSection>

      <AssignmentAddonsSection assignment={assignment} order={order} mainProductId={mainProductId} />

    </Stack>
  )
}
